import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../api';
import { useSessao, veTodasAsEmpresas, type DadosSessao } from '../sessao';

/** Campo de dias: a chave da API, o teto e o rótulo (`notificacoes.antecedencia.<rotulo>`). */
interface CampoDias {
  id: 'pagar' | 'receber' | 'contrato';
  max: number;
}
const CONTAS: CampoDias[] = [
  { id: 'pagar', max: 60 },
  { id: 'receber', max: 60 },
];
const CONTRATO: CampoDias = { id: 'contrato', max: 180 };

const valido = (v: string, max: number) => /^\d{1,3}$/.test(v) && Number(v) <= max;

/**
 * Review r42 spec (B2): quem não vê todas as empresas não muda o padrão do workspace (as outras empresas o herdam; o
 * servidor recusa com `permissao.padrao_workspace_exige_todas`): a escolha mostra só as empresas dele e parte da atual.
 */
export function escolhaDeEmpresaAntecedencia(s: Pick<DadosSessao, 'empresas'>) {
  const opcoes = s.empresas?.opcoes ?? [];
  const soEmpresa = !veTodasAsEmpresas(s) && opcoes.length > 0;
  const inicial = soEmpresa ? (opcoes.find((x) => x.id === s.empresas?.atual) ?? opcoes[0]!).id : '';
  return { mostra: opcoes.length > 1 || soEmpresa, comPadrao: !soEmpresa, inicial };
}

/** Quem vê o cartão: quem configura o Contas ou edita o Assinaturas (com o módulo instalado). */
export function useMostraAntecedencia() {
  const s = useSessao();
  return (s.instalado('contas') && s.pode('contas.configurar')) || (s.instalado('assinaturas') && s.pode('assinaturas.configurar'));
}

/** Chaves das consultas (a empresa no fim; '' = o padrão do workspace). Invalidar o prefixo pega todas as empresas. */
export const CHAVE_DIAS_CONTAS = ['contas', 'notificacoes', 'dias'] as const;
export const CHAVE_DIAS_CONTRATO = ['assinaturas', 'notificacoes', 'dias'] as const;

/**
 * Fase 2 (W3, W22 K8): antecedência dos avisos de vencimento por workspace. Contas a pagar e faturas a vencer (Contas,
 * `contas.configurar`) e contratos perto do fim (Assinaturas, `assinaturas.configurar` desde o val5); cada campo só aparece a quem pode
 * mudar e com o módulo instalado. 0 desliga o aviso. Aparece em Contas > Cobranças e em Minha conta > Notificações >
 * Avisos do workspace.
 *
 * B7 (lote 09/10, item 5): o aviso é só da equipe (o texto diz isso e aponta a régua de cobrança para o cliente) e a
 * antecedência pode ser própria de uma empresa: com mais de uma empresa, o seletor escolhe entre o padrão do workspace e
 * cada empresa; a empresa sem valor próprio segue o padrão, e "Voltar ao padrão do workspace" tira o valor próprio.
 */
export function AntecedenciaAvisos({ linkCobrancas = false }: { linkCobrancas?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const comContas = s.instalado('contas') && s.pode('contas.configurar');
  // Val5 (29/10): a antecedência dos contratos é configuração das Assinaturas (`assinaturas.configurar`).
  const comContrato = s.instalado('assinaturas') && s.pode('assinaturas.configurar');
  const empresas = s.empresas?.opcoes ?? [];
  const escolha = escolhaDeEmpresaAntecedencia(s);
  const [empresa, setEmpresa] = useState(escolha.inicial);
  const params = empresa ? { empresa_id: empresa } : {};
  const contas = useQuery({
    queryKey: [...CHAVE_DIAS_CONTAS, empresa],
    queryFn: () => get<{ dias: number; dias_receber: number; proprio?: boolean }>('/contas/notificacoes/dias', params),
    enabled: comContas,
  });
  const contrato = useQuery({ queryKey: [...CHAVE_DIAS_CONTRATO, empresa], queryFn: () => get<{ dias: number; proprio?: boolean }>('/assinaturas/notificacoes/dias', params), enabled: comContrato });
  const [mudados, setMudados] = useState<Partial<Record<CampoDias['id'], string>>>({});
  const gravado = { pagar: contas.data?.dias, receber: contas.data?.dias_receber, contrato: contrato.data?.dias };
  const valor = (c: CampoDias) => mudados[c.id] ?? (gravado[c.id] === undefined ? '' : String(gravado[c.id]));
  const campos = [...(comContas ? CONTAS : []), ...(comContrato ? [CONTRATO] : [])];
  const invalidos = campos.filter((c) => c.id in mudados && !valido(valor(c), c.max));
  const proprio = (comContas && !!contas.data?.proprio) || (comContrato && !!contrato.data?.proprio);
  const atualizar = async () => {
    setMudados({});
    toast.sucesso(t('comum.salvo'));
    await qc.invalidateQueries({ queryKey: CHAVE_DIAS_CONTAS });
    await qc.invalidateQueries({ queryKey: CHAVE_DIAS_CONTRATO });
  };
  const salvar = useMutation({
    mutationFn: async () => {
      const n = (id: CampoDias['id']) => (mudados[id] === undefined ? undefined : Number(mudados[id]));
      if (comContas && (n('pagar') !== undefined || n('receber') !== undefined)) await api('PUT', '/contas/notificacoes/dias', { dias: n('pagar'), dias_receber: n('receber'), ...params });
      if (comContrato && n('contrato') !== undefined) await api('PUT', '/assinaturas/notificacoes/dias', { dias: n('contrato'), ...params });
    },
    onSuccess: atualizar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const herdar = useMutation({
    mutationFn: async () => {
      if (comContas && contas.data?.proprio) await api('PUT', '/contas/notificacoes/dias', { empresa_id: empresa, herdar: true });
      if (comContrato && contrato.data?.proprio) await api('PUT', '/assinaturas/notificacoes/dias', { empresa_id: empresa, herdar: true });
    },
    onSuccess: atualizar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!campos.length) return null;
  return (
    <Cartao titulo={t('notificacoes.antecedencia.titulo')}>
      <div data-antecedencia-avisos className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">
          {t('notificacoes.antecedencia.texto')} {t('notificacoes.antecedencia.cliente')}{' '}
          {linkCobrancas && s.instalado('contas') && s.pode('contas.ver') && (
            <Link to="/financeiro/contas/cobrancas" className="font-medium text-acento-forte hover:underline">
              {t('notificacoes.antecedencia.abrirCobrancas')}
            </Link>
          )}
        </p>
        {escolha.mostra && (
          <div className="flex flex-col gap-2" data-antecedencia-empresa>
            {/* Review r43 visual (M2): a largura fica no invólucro, não no select (com ela no select, a seta do Selecao, presa
                ao contêiner relativo de largura inteira, ficava longe do campo). */}
            <div className="min-w-0 sm:max-w-sm">
            <Rotulo id="antecedencia-empresa" rotulo={t('notificacoes.antecedencia.empresa')}>
              <Selecao
                id="antecedencia-empresa"
                value={empresa}
                onChange={(e) => {
                  setEmpresa(e.target.value);
                  setMudados({});
                }}
                opcoes={[...(escolha.comPadrao ? [{ valor: '', rotulo: t('notificacoes.antecedencia.padraoWorkspace') }] : []), ...empresas.map((x) => ({ valor: x.id, rotulo: x.nome }))]}
              />
            </Rotulo>
            </div>
            {empresa && (contas.data || contrato.data) && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-tinta-3">
                {proprio ? <Selo>{t('notificacoes.antecedencia.proprio')}</Selo> : <span>{t('notificacoes.antecedencia.herdado')}</span>}
                {proprio && (
                  <button type="button" onClick={() => herdar.mutate()} disabled={herdar.isPending} className="text-xs text-acento-forte underline">
                    {t('notificacoes.antecedencia.voltarWorkspace')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {campos.map((c) => {
            const id = `antecedencia-${c.id}`;
            const erro = invalidos.includes(c) ? t('notificacoes.antecedencia.faixa', { max: c.max }) : undefined;
            return (
              <Rotulo key={c.id} id={id} rotulo={t(`notificacoes.antecedencia.${c.id}`)} erro={erro}>
                <Entrada
                  id={id}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={c.max}
                  value={valor(c)}
                  aria-invalid={erro ? true : undefined}
                  aria-describedby={erro ? `${id}-erro` : undefined}
                  onChange={(e) => setMudados((m) => ({ ...m, [c.id]: e.target.value }))}
                  className="numero"
                />
              </Rotulo>
            );
          })}
        </div>
        <div>
          <Botao variante="secundario" disabled={!Object.keys(mudados).length || invalidos.length > 0} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {t('comum.salvar')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}
